import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import {
  REPORTING_TYPES,
  changeManager,
  employeeLabelOf,
  getReportingChain,
  getReportingHierarchy,
  listEmployees,
  type EmployeeRow,
  type ReportingNode,
  type ReportingType,
} from '../../api/orgApi.js';
import { Field, SelectField, sx, toOptions, todayIso } from './orgKit.js';

export function HierarchyPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [rootEmployeeId, setRootEmployeeId] = useState('');
  const [nodes, setNodes] = useState<ReportingNode[]>([]);
  const [chain, setChain] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [showChange, setShowChange] = useState(false);
  const [managerId, setManagerId] = useState('');
  const [reportingType, setReportingType] = useState<ReportingType>('PRIMARY');
  const [effectiveFromDate, setEffectiveFromDate] = useState(todayIso());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listEmployees(token).then((r) => setEmployees(r.data)).catch((e) => setError((e as Error).message));
  }, [token]);

  const empName = useMemo(
    () => Object.fromEntries(employees.map((e) => [e.empExtId, employeeLabelOf(e)])),
    [employees],
  );
  const nameOf = (id: string): string => empName[id] ?? id;

  useEffect(() => {
    if (!rootEmployeeId) { setNodes([]); setChain([]); return; }
    setLoading(true);
    Promise.all([
      getReportingHierarchy(token, rootEmployeeId).then((h) => setNodes(h.nodes ?? [])).catch((e) => setError((e as Error).message)),
      getReportingChain(token, rootEmployeeId).then((c) => setChain(Array.isArray(c) ? c : [])).catch(() => setChain([])),
    ]).finally(() => setLoading(false));
  }, [rootEmployeeId, token]);

  function reload(): void {
    if (!rootEmployeeId) return;
    // The manager was changed; a failed re-read must not leave the old tree
    // on screen as if it were current.
    getReportingHierarchy(token, rootEmployeeId).then((h) => setNodes(h.nodes ?? [])).catch((e) => setError((e as Error).message));
    getReportingChain(token, rootEmployeeId).then((c) => setChain(Array.isArray(c) ? c : [])).catch((e) => setError((e as Error).message));
  }

  async function submitChange(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    if (!rootEmployeeId) { setError(t('org.hierarchy.selectFirst')); return; }
    if (!managerId) { setError(t('org.employee.actions.selectManager')); return; }
    if (managerId === rootEmployeeId) { setError(t('org.hierarchy.selfReport')); return; }
    setError(null);
    setBusy(true);
    try {
      await changeManager(token, rootEmployeeId, { managerId, reportingType, effectiveFromDate });
      setNotice(t('org.hierarchy.managerUpdated'));
      setShowChange(false);
      setManagerId('');
      reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const employeeOptions = employees.map((e) => ({ value: e.empExtId, label: employeeLabelOf(e) }));

  return (
    <main aria-label="hierarchy" style={sx.main}>
      <div style={sx.headerRow}>
        <h1>{t('org.hierarchy.title')}</h1>
        {rootEmployeeId && (
          <Button type="button" variant="primary" onClick={() => { setShowChange((v) => !v); setNotice(null); }}>
            {showChange ? t('org.common.close') : t('org.hierarchy.changeManager')}
          </Button>
        )}
      </div>
      <p style={{ color: 'var(--color-text-secondary)', marginTop: -8 }}>
        {t('org.hierarchy.dedicatedViews')}{' '}
        <Link to="/org/territories" style={sx.link}>{t('org.hierarchy.territoryTree')}</Link> ·{' '}
        <Link to="/org/stakeholders" style={sx.link}>{t('org.hierarchy.stakeholders')}</Link>
      </p>

      <div style={{ maxWidth: 460, marginBottom: 16 }}>
        <SelectField
          label={t('org.hierarchy.rootLabel')}
          name="rootEmployeeId"
          value={rootEmployeeId}
          onChange={(v) => { setRootEmployeeId(v); setShowChange(false); setNotice(null); setError(null); }}
          options={employeeOptions}
          placeholder={t('org.hierarchy.rootPh')}
        />
      </div>

      {error && <p role="alert" style={sx.error}>{error}</p>}
      {notice && <p style={sx.ok}>{notice}</p>}

      {showChange && (
        <form onSubmit={submitChange} style={sx.card}>
          <SelectField label={t('org.hierarchy.newManager')} name="managerId" value={managerId} onChange={setManagerId} options={employeeOptions} required />
          <SelectField label={t('org.field.reportingType')} name="reportingType" value={reportingType} onChange={(v) => setReportingType(v as ReportingType)} options={toOptions(REPORTING_TYPES)} required />
          <Field label={t('org.field.effectiveFrom')} name="effectiveFromDate" value={effectiveFromDate} onChange={setEffectiveFromDate} type="date" required />
          <div style={sx.actionBar}>
            <Button type="submit" variant="primary" disabled={busy}>{busy ? t('org.kit.saving') : t('org.hierarchy.updateManager')}</Button>
            <Button type="button" variant="secondary" onClick={() => setShowChange(false)}>{t('common.cancel')}</Button>
          </div>
        </form>
      )}

      {rootEmployeeId && chain.length > 0 && (
        <p style={{ marginBottom: 16 }}>
          <strong>{t('org.hierarchy.reportsUpTo')}</strong>{' '}
          {chain.map((id, i) => (
            <span key={id}>{i > 0 ? ' → ' : ''}{nameOf(id)}</span>
          ))}
        </p>
      )}

      {!rootEmployeeId && <p>{t('org.hierarchy.selectToView')}</p>}
      {rootEmployeeId && loading && <p>{t('common.loading')}</p>}
      {rootEmployeeId && !loading && nodes.length === 0 && <p>{t('org.hierarchy.noReports')}</p>}
      {rootEmployeeId && nodes.length > 0 && (
        <DataTable<ReportingNode>
          rowKey={(n) => n.employeeId}
          rows={nodes}
          columns={[
            { key: 'employee', header: t('org.col.employee'), render: (n) => <span style={{ paddingLeft: n.depth * 20 }}>{nameOf(n.employeeId)}</span> },
            { key: 'reportsTo', header: t('org.col.reportsTo'), render: (n) => nameOf(n.managerId) },
            { key: 'depth', header: t('org.col.depth'), render: (n) => n.depth },
          ]}
        />
      )}
    </main>
  );
}
